package org.multiplatform.kotfolio.feature

import kotlinx.collections.immutable.persistentListOf
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import org.multiplatform.kotfolio.domain.Language
import org.multiplatform.kotfolio.domain.Topic
import org.multiplatform.kotfolio.domain.usecase.GetLanguageUseCase
import org.multiplatform.kotfolio.domain.usecase.GetTopicForLanguageUseCase
import org.multiplatform.kotfolio.domain.usecase.GetTracksForTopicUseCase
import org.multiplatform.kotfolio.models.UIModel

@OptIn(ExperimentalCoroutinesApi::class)
class BlogDiscoveryUIModel(
    override val scope: CoroutineScope,
    getLanguageUseCase: GetLanguageUseCase,
    getTopicForLanguageUseCase: GetTopicForLanguageUseCase,
    getTracksForTopicUseCase: GetTracksForTopicUseCase
) : UIModel<BlogDiscoveryUIState, BlogDiscoveryCommand> {
    private val languages = getLanguageUseCase()

    // --- Internal mutable state (user selections) ---
    private val selectLanguage = MutableStateFlow<Language?>(null)
    private val selectTopic = MutableStateFlow<Topic?>(null)

    private val topics = selectLanguage.flatMapLatest { language ->
        language?.let { getTopicForLanguageUseCase(it.id) }
            ?: flowOf(persistentListOf())
    }

    private val tracks = selectTopic.flatMapLatest { topic ->
        topic?.let { getTracksForTopicUseCase(it.id) }
            ?: flowOf(persistentListOf())
    }

    // --- Public state: combine wires everything together ---
    override val uiState: StateFlow<BlogDiscoveryUIState> = combine(
        languages,
        selectLanguage,
        topics,
        selectTopic,
        tracks,
        ::BlogDiscoveryUIState
    ).stateIn(scope, SharingStarted.WhileSubscribed(5_000), BlogDiscoveryUIState.Default)

    // --- Commands: update selections, reset downstream when parent changes ---
    override fun sendCommand(command: BlogDiscoveryCommand) {
        when (command) {
            is BlogDiscoveryCommand.SelectLanguage -> {
                selectLanguage.value = command.language
                selectTopic.value = null
            }

            is BlogDiscoveryCommand.SelectTopic -> {
                selectTopic.value = command.topic
            }
        }
    }
}